import { SharedUiColorDerivation, SharedUiColorName } from '@/shared/ui/theming';
import { IconName, UiIconComponent } from '@/ui/icon/ui-icon';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import {
    SharedUiColoredBackgroundColorPipe,
    SharedUiTextOnColoredBackgroundColorPipe
} from '../../libs/shared/ui/theming/lib/colors/shared-ui-color.pipe';

export const Status = {
    INFO: 'info',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error'
} as const;
export type Status = LiteralValues<typeof Status>;

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./advizeo-status-message.scss'],
    selector: 'advizeo-status-message',
    templateUrl: 'advizeo-status-message.html',
    providers: [SharedUiColoredBackgroundColorPipe],
    imports: [UiIconComponent, AsyncPipe, SharedUiTextOnColoredBackgroundColorPipe],
    host: {
        '[style.background-color]': 'bgColor()'
    }
})
export class AdvizeoStatusMessageComponent {
    private _sharedUiColoredBackgroundColorPipe = inject(SharedUiColoredBackgroundColorPipe);

    message = input.required<string>();
    status = input<Status>('info');

    iconName = computed(() => this.getIconNameByStatus(this.status()));
    statusColor = computed(() => this.getStatusColor(this.status()));

    bgColor = toSignal(
        toObservable(this.statusColor).pipe(
            switchMap(color => this._sharedUiColoredBackgroundColorPipe.transform(color, { derivation: SharedUiColorDerivation.LIGHT }))
        )
    );

    private getIconNameByStatus(status: Status) {
        switch (status) {
            case 'info':
                return IconName.INFO;
            case 'success':
                return IconName.SUCCESS;
            case 'warning':
                return IconName.WARNING;
            case 'error':
                return IconName.WARNING;
        }
    }

    private getStatusColor(status: Status): SharedUiColorName {
        switch (status) {
            case 'info':
                return SharedUiColorName.INFO;
            case 'success':
                return SharedUiColorName.SUCCESS;
            case 'warning':
                return SharedUiColorName.DANGER;
            case 'error':
                return SharedUiColorName.FAILURE;
        }
    }
}
